import { Box, BoxProps, Img, ImgProps } from '@chakra-ui/react'
import { CSSProperties, FC, useCallback, useEffect, useRef, useState } from 'react'
import { isAppHydrated } from 'utils/appHydration'
import { preventImageDrag } from 'utils/preventImageDrag'
import { useSsrContent } from 'utils/ssrContent'

export interface ImageBoxProps extends BoxProps {
  src: string
  alt: string
  withGradientOverlay?: boolean
  imgObjectFit?: CSSProperties['objectFit']
  shouldTransitionOpacity?: boolean
  loading?: 'lazy' | 'eager'
  imgProps?: ImgProps
}

const ImageBox: FC<ImageBoxProps> = ({
  src,
  alt,
  children,
  withGradientOverlay = false,
  imgObjectFit = 'cover',
  loading = 'lazy',
  shouldTransitionOpacity = true,
  imgProps,
  ...rest
}) => {
  // Server-rendered page, first load: the image is in the server HTML and may load before React hydrates (its
  // onLoad would be missed), so it is shown at once, without the fade. Mounted later, it fades in on load.
  const ssrContent = useSsrContent()
  const [isLoaded, setIsLoaded] = useState(() => ssrContent && !isAppHydrated())
  const imgRef = useRef<HTMLImageElement>(null)

  const handleLoadingComplete = useCallback(() => {
    setIsLoaded(true)
  }, [])

  useEffect(() => {
    // Loaded before this effect ran (from the cache, or before hydration): onLoad won't fire again.
    if (imgRef.current?.complete && imgRef.current.naturalWidth) setIsLoaded(true)
  }, [])

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isLoaded) setIsLoaded(true)
    }, 5000)

    return () => clearTimeout(timeout)
  }, [isLoaded])

  if (!src) return null

  return (
    <Box
      position="relative"
      overflow="hidden"
      opacity={shouldTransitionOpacity ? (isLoaded ? 1 : 0) : 1}
      transition="opacity 250ms ease-out"
      {...rest}
    >
      {withGradientOverlay && (
        <Box
          position="absolute"
          top={0}
          left={0}
          w="100%"
          h="100%"
          bg="linear-gradient(transparent, black)"
          zIndex="docked"
        />
      )}
      <Img
        {...{ src, alt }}
        ref={imgRef}
        onLoad={handleLoadingComplete}
        pos="absolute"
        top={0}
        left={0}
        w="full"
        h="full"
        objectFit={imgObjectFit}
        {...{ loading }}
        {...preventImageDrag}
        {...imgProps}
      />
      {children}
    </Box>
  )
}

export default ImageBox
